// apps/client/src/pages/reports/BrokerBookTrendReportPage.tsx
// Book Trend Report — Earnout / Retention Tracker.
//
// Every label on this page is configurable per tenant via
// `companies/{c}.bookTrendReportConfig` so the same report can serve any
// vendor / outgoing partner / earnout scenario without code changes.

import React, { useCallback, useMemo, useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import {
  LineChart as LineChartIcon,
  Loader2,
  AlertTriangle,
  ChevronDown,
  ChevronUp,
  Search,
} from 'lucide-react';

import PageHeader from '../../components/common/PageHeader';
import { formatCompactCurrency, formatCurrency } from '../../utils/formatters';
import { useAuth } from '../../hooks/useAuth';
import { BROKER_FILTERED_ADMIN_COPY } from '../../utils/brokerFilteredAdmin';
import {
  useBrokerBookTrend,
  useSetLoanDisposition,
} from '../../hooks/useBrokerBookTrend';
import type {
  BalanceGridRow,
  ClawbackRow,
  DroppedOffLoan,
  GetBrokerBookTrendResponse,
  ManualDisposition,
  PostSaleActivityRow,
  ReinstatedLoan,
  ResolvedDisposition,
  UpfrontCommissionRow,
} from '../../types/brokerBookTrend';

// ============================================================================
// FORMATTERS
// ============================================================================

const BRISBANE_FMT = new Intl.DateTimeFormat('en-AU', {
  timeZone: 'Australia/Brisbane',
  dateStyle: 'medium',
  timeStyle: 'short',
});

const fmtMonthLabel = (yyyymm: string): string => {
  if (!/^\d{4}-\d{2}$/.test(yyyymm)) return yyyymm;
  const [y, m] = yyyymm.split('-');
  return new Date(Number(y), Number(m) - 1).toLocaleDateString('en-AU', {
    month: 'short',
    year: '2-digit',
  });
};

const fmtMonthLong = (yyyymm: string): string => {
  if (!/^\d{4}-\d{2}$/.test(yyyymm)) return yyyymm;
  const [y, m] = yyyymm.split('-');
  return new Date(Number(y), Number(m) - 1).toLocaleDateString('en-AU', {
    month: 'long',
    year: 'numeric',
  });
};

// Cards: the app-wide three-significant-figure rule. Tables keep formatCurrency.
const fmtMoneyShort = (cents: number): string => formatCompactCurrency(cents);

const fmtMoneyDelta = (cents: number): string => {
  if (cents === 0) return '$0';
  const sign = cents > 0 ? '+' : '-';
  return `${sign}$${(Math.abs(cents) / 100).toLocaleString('en-US', { maximumFractionDigits: 0 })}`;
};

const fmtTimestamp = (iso: string): string => {
  try {
    return BRISBANE_FMT.format(new Date(iso));
  } catch {
    return iso;
  }
};

// ============================================================================
// HELPERS — disposition labels
// ============================================================================

const DISPOSITION_LABEL: Record<ResolvedDisposition, string> = {
  discharged: '✓ Discharged',
  refinanced_internal: '↔ Refinanced (internal)',
  refinanced_external: '↔ Refinanced externally',
  active_zero_balance: '💤 Active, zero balance',
  other: '✎ Other',
  needs_review: '⚠ Needs review',
};

const DISPOSITION_COLOR: Record<ResolvedDisposition, string> = {
  discharged: 'text-emerald-300 bg-emerald-500/10 border-emerald-500/30',
  refinanced_internal: 'text-blue-300 bg-blue-500/10 border-blue-500/30',
  refinanced_external: 'text-blue-300 bg-blue-500/10 border-blue-500/30',
  active_zero_balance: 'text-cyan-300 bg-cyan-500/10 border-cyan-500/30',
  other: 'text-gray-300 bg-gray-500/10 border-gray-500/30',
  needs_review: 'text-amber-300 bg-amber-500/10 border-amber-500/40',
};

// ============================================================================
// PRIMITIVE COMPONENTS
// ============================================================================

const SectionShell: React.FC<{
  title: string;
  subtitle?: string;
  accent?: 'default' | 'blue' | 'rose' | 'emerald' | 'amber';
  rightSlot?: React.ReactNode;
  /** Whether the body can be hidden/shown via a chevron in the header. */
  collapsible?: boolean;
  /** Initial open state when collapsible. Default open. */
  defaultOpen?: boolean;
  children: React.ReactNode;
}> = ({
  title,
  subtitle,
  accent = 'default',
  rightSlot,
  collapsible = false,
  defaultOpen = true,
  children,
}) => {
  const [open, setOpen] = useState<boolean>(defaultOpen);
  const isOpen = collapsible ? open : true;
  const border =
    accent === 'blue'
      ? 'border-blue-500/30'
      : accent === 'rose'
        ? 'border-rose-500/30'
        : accent === 'emerald'
          ? 'border-emerald-500/30'
          : accent === 'amber'
            ? 'border-amber-500/30'
            : 'border-white/10';
  return (
    <section className={`bg-gray-800/50 border ${border} rounded-xl p-5`}>
      <div className="flex items-start justify-between gap-4 mb-3">
        <button
          type="button"
          onClick={collapsible ? () => setOpen((o) => !o) : undefined}
          className={`flex-1 text-left flex items-start gap-2 ${collapsible ? 'cursor-pointer hover:opacity-90' : 'cursor-default'}`}
          aria-expanded={collapsible ? isOpen : undefined}
        >
          {collapsible && (
            <span className="mt-0.5 text-gray-400">
              {isOpen ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
            </span>
          )}
          <div>
            <h3 className="text-sm font-semibold text-white">{title}</h3>
            {subtitle && <p className="text-xs text-gray-400 mt-0.5">{subtitle}</p>}
          </div>
        </button>
        {rightSlot}
      </div>
      {isOpen && children}
    </section>
  );
};

const StatCard: React.FC<{
  label: string;
  value: string;
  sub?: string;
  tone?: 'default' | 'positive' | 'negative' | 'warning';
}> = ({ label, value, sub, tone = 'default' }) => {
  const tone_cls =
    tone === 'positive'
      ? 'text-emerald-300'
      : tone === 'negative'
        ? 'text-rose-300'
        : tone === 'warning'
          ? 'text-amber-300'
          : 'text-white';
  return (
    <div className="bg-gray-800/50 border border-white/10 rounded-xl p-4">
      <div className="text-[0.625rem] uppercase tracking-wider text-gray-500 font-semibold">
        {label}
      </div>
      <div className={`mt-2 text-2xl font-bold tabular-nums ${tone_cls}`}>{value}</div>
      {sub && <div className="mt-1 text-xs text-gray-400 tabular-nums">{sub}</div>}
    </div>
  );
};

const EmptyState: React.FC<{ message: string }> = ({ message }) => (
  <div className="bg-gray-900/40 rounded-lg p-6 text-center">
    <p className="text-sm text-gray-400">{message}</p>
  </div>
);

/**
 * Search-and-filter bar shown in the right-slot of any section that holds
 * a table. Matches the VALUTTEN convention: free-text search + optional
 * dropdown filter + row count.
 */
const TableFilterBar: React.FC<{
  search: string;
  onSearchChange: (v: string) => void;
  placeholder?: string;
  filterValue?: string;
  filterOptions?: Array<{ value: string; label: string }>;
  onFilterChange?: (v: string) => void;
}> = ({ search, onSearchChange, placeholder, filterValue, filterOptions, onFilterChange }) => (
  <div className="flex items-center gap-2 flex-shrink-0">
    <div className="relative">
      <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3 h-3 text-gray-500 pointer-events-none" />
      <input
        type="text"
        placeholder={placeholder ?? 'Search…'}
        value={search}
        onChange={(e) => onSearchChange(e.target.value)}
        className="pl-6 pr-2 py-1 text-xs bg-gray-900 border border-gray-700 rounded text-white w-56"
      />
    </div>
    {filterOptions && onFilterChange && (
      <select
        value={filterValue ?? ''}
        onChange={(e) => onFilterChange(e.target.value)}
        className="px-2 py-1 text-xs bg-gray-900 border border-gray-700 rounded text-white"
      >
        {filterOptions.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
    )}
  </div>
);

// ============================================================================
// DISPOSITION DROPDOWN (with free-text "Other" note)
// ============================================================================

const DispositionControl: React.FC<{
  loan: DroppedOffLoan;
  canEdit: boolean;
  onSet: (loanId: string, disposition: ManualDisposition, note?: string) => Promise<void>;
}> = ({ loan, canEdit, onSet }) => {
  // `setLoanDisposition` does NOT opt in to allowBrokerFilteredAdmin, so it is
  // default-denied to a broker-restricted admin at the chokepoint.
  const { isBrokerFilteredAdmin } = useAuth();
  const [saving, setSaving] = useState(false);
  const [showNote, setShowNote] = useState<boolean>(
    loan.override?.disposition === 'other',
  );
  const [noteDraft, setNoteDraft] = useState<string>(loan.override?.note ?? '');
  const effective = loan.effectiveDisposition;
  const isAutoLocked =
    !loan.override &&
    (effective === 'discharged' || effective === 'refinanced_internal');

  if (!canEdit || isAutoLocked) {
    const cls = DISPOSITION_COLOR[effective];
    return (
      <span
        className={`inline-flex items-center px-2 py-0.5 rounded text-[0.7rem] font-semibold border ${cls}`}
      >
        {DISPOSITION_LABEL[effective]}
      </span>
    );
  }

  const handleChange = async (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value as ManualDisposition | 'needs_review';
    if (value === 'needs_review') return;
    if (value === 'other') {
      // Reveal note input but don't save until user submits a note.
      setShowNote(true);
      return;
    }
    setSaving(true);
    try {
      await onSet(loan.loanId, value);
      setShowNote(false);
    } finally {
      setSaving(false);
    }
  };

  const handleSaveNote = async () => {
    const trimmed = noteDraft.trim();
    if (!trimmed) return; // Don't save empty notes
    setSaving(true);
    try {
      await onSet(loan.loanId, 'other', trimmed);
    } finally {
      setSaving(false);
    }
  };

  const cls = DISPOSITION_COLOR[effective];

  return (
    <div className="flex flex-col gap-1 min-w-[12rem]">
      <div className="inline-flex items-center gap-2">
        <select
          value={loan.override?.disposition ?? 'needs_review'}
          onChange={handleChange}
          disabled={isBrokerFilteredAdmin || saving}
          title={isBrokerFilteredAdmin ? BROKER_FILTERED_ADMIN_COPY : undefined}
          className={`rounded px-1.5 py-0.5 text-[0.7rem] font-semibold border cursor-pointer disabled:opacity-50 ${cls}`}
        >
          <option value="needs_review">⚠ Needs review</option>
          <option value="discharged">✓ Discharged (lender confirmed)</option>
          <option value="refinanced_external">↔ Refinanced externally</option>
          <option value="active_zero_balance">💤 Active, zero balance</option>
          <option value="other">✎ Other (add a note)</option>
        </select>
        {saving && <Loader2 className="w-3 h-3 animate-spin text-gray-400" />}
      </div>
      {showNote && (
        <div className="flex items-center gap-1">
          <input
            type="text"
            value={noteDraft}
            onChange={(e) => setNoteDraft(e.target.value)}
            placeholder="What happened? (e.g. sold property, divorce, etc.)"
            maxLength={500}
            className="flex-1 px-1.5 py-0.5 text-[0.7rem] bg-gray-900 border border-gray-700 rounded text-white placeholder-gray-500"
            onKeyDown={(e) => {
              if (e.key === 'Enter') void handleSaveNote();
            }}
          />
          <button
            type="button"
            onClick={() => void handleSaveNote()}
            disabled={!noteDraft.trim() || saving}
            className="px-2 py-0.5 text-[0.7rem] font-semibold bg-blue-600 hover:bg-blue-500 disabled:opacity-40 disabled:cursor-not-allowed rounded text-white"
          >
            Save
          </button>
        </div>
      )}
      {loan.override?.disposition === 'other' && loan.override.note && !showNote && (
        <p className="text-[0.65rem] text-gray-400 italic">
          “{loan.override.note}”
        </p>
      )}
    </div>
  );
};

// ============================================================================
// MAIN PAGE
// ============================================================================

interface OutletCtx {
  userData?: { role?: string; companyId?: string };
}

const BrokerBookTrendReportPage: React.FC = () => {
  const ctx = useOutletContext<OutletCtx>();
  const role = ctx?.userData?.role ?? '';
  const companyId = ctx?.userData?.companyId ?? null;
  const isAdmin =
    role === 'super_admin' || role === 'administrator' || role === 'office_admin';

  const [selectedPeriod, setSelectedPeriod] = useState<string | null>(null);
  const { data, loading, error, refetch } = useBrokerBookTrend({
    companyId,
    periodMonth: selectedPeriod,
  });
  const { setDisposition } = useSetLoanDisposition(companyId);

  const handleSetDisposition = useCallback(
    async (loanId: string, disposition: ManualDisposition, note?: string) => {
      await setDisposition({ loanId, disposition, note });
      await refetch();
    },
    [setDisposition, refetch],
  );

  if (!companyId) {
    return (
      <div className="space-y-6">
        <PageHeader
          icon={LineChartIcon}
          title="Book Trend Report"
          subtitle="Loading company context…"
        />
      </div>
    );
  }

  // Pull configurable labels with safe defaults (handles old responses
  // during the deploy window before backend ships labels).
  const labels = data?.labels ?? {
    pageTitle: 'Book Trend Report',
    pageSubtitle: "Retention of the previous owner's book, evaluated automatically each month.",
    relationshipLabel: 'Previous owner',
    agreementType: 'Earnout',
    saleVerb: 'sold to',
  };

  return (
    <div className="space-y-6">
      <PageHeader
        icon={LineChartIcon}
        title={labels.pageTitle}
        subtitle={labels.pageSubtitle}
        onRefresh={() => void refetch()}
        isRefreshing={loading}
      />

      {error && (
        <div className="bg-rose-500/10 border border-rose-500/30 text-rose-300 px-4 py-3 rounded-lg text-sm">
          <p className="font-semibold mb-1">Couldn't load the report</p>
          <p className="text-xs">{error.message}</p>
        </div>
      )}

      {!data && loading && !error && (
        <div className="bg-gray-800/50 border border-white/10 rounded-xl p-6 text-center">
          <Loader2 className="w-5 h-5 animate-spin text-gray-400 mx-auto" />
          <p className="text-sm text-gray-400 mt-2">Loading retention data…</p>
        </div>
      )}

      {data && (
        <BookTrendBody
          data={data}
          isAdmin={isAdmin}
          selectedPeriod={selectedPeriod}
          onSelectPeriod={setSelectedPeriod}
          onSetDisposition={handleSetDisposition}
        />
      )}
    </div>
  );
};

// ============================================================================
// BODY
// ============================================================================

const BookTrendBody: React.FC<{
  data: GetBrokerBookTrendResponse;
  isAdmin: boolean;
  selectedPeriod: string | null;
  onSelectPeriod: (m: string) => void;
  onSetDisposition: (
    loanId: string,
    disposition: ManualDisposition,
    note?: string,
  ) => Promise<void>;
}> = ({ data, isAdmin, selectedPeriod, onSelectPeriod, onSetDisposition }) => {
  const { previousOwner, summary, retentionCurve, balanceGrid, monthsCovered, labels } = data;
  const effectivePeriod = selectedPeriod ?? data.periodMonth;
  const periodLong = fmtMonthLong(data.periodMonth);
  // Fallback for backend responses pre-labels deploy — keep dropdown stable.
  const dropdownMonths = data.availableMonths ?? data.monthsCovered;

  return (
    <>
      {/* HOW-IT-WORKS BANNER */}
      <section className="bg-emerald-500/5 border border-emerald-500/30 rounded-xl p-4">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 text-xs">
          <div>
            <div className="text-[0.625rem] uppercase tracking-wider text-emerald-400 font-semibold mb-1">
              Source of the numbers
            </div>
            <div className="text-white font-semibold">Your monthly commission data</div>
            <div className="text-gray-400 mt-0.5">Every loan in the business — no broker filter</div>
          </div>
          <div>
            <div className="text-[0.625rem] uppercase tracking-wider text-emerald-400 font-semibold mb-1">
              Sale date
            </div>
            <div className="text-white font-semibold">{fmtMonthLong(previousOwner.saleMonth)}</div>
            <div className="text-gray-400 mt-0.5">Baseline locked at this month</div>
          </div>
          <div>
            <div className="text-[0.625rem] uppercase tracking-wider text-emerald-400 font-semibold mb-1">
              How it's calculated
            </div>
            <div className="text-white font-semibold">Automatic, every load</div>
            <div className="text-gray-400 mt-0.5">No manual classification or sorting needed</div>
          </div>
          <div>
            <div className="text-[0.625rem] uppercase tracking-wider text-emerald-400 font-semibold mb-1">
              Manual uploads required
            </div>
            <div className="text-emerald-300 font-semibold">None</div>
            <div className="text-gray-400 mt-0.5">No more monthly spreadsheet</div>
          </div>
        </div>
      </section>

      {/* AGREEMENT CONDITIONS (placeholder until terms loaded) */}
      {!data.earnout.configured && (
        <section className="bg-amber-500/5 border border-amber-500/30 rounded-xl p-4">
          <div className="flex items-start gap-2">
            <AlertTriangle className="w-4 h-4 text-amber-400 mt-0.5 flex-shrink-0" />
            <div className="flex-1">
              <h3 className="text-sm font-semibold text-amber-300">
                {labels.agreementType} conditions not yet configured
              </h3>
              <p className="text-xs text-amber-200/80 mt-1">
                Once the sale agreement terms are loaded (retention floor, time horizon, payment
                milestones), this panel will surface contractual thresholds against the retention
                curve below — flagging risk when retention dips below the agreed floor. Until then,
                the report shows raw retention metrics only.
              </p>
            </div>
          </div>
        </section>
      )}

      {/* SELECTION */}
      <SectionShell
        title="Report selection"
        subtitle={`Pick a reporting month. The sale date (${fmtMonthLong(previousOwner.saleMonth)}) is locked as the starting point.`}
      >
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-[0.625rem] uppercase tracking-wider text-gray-500 font-semibold mb-2">
              {labels.relationshipLabel}
            </label>
            <div className="w-full px-3 py-2 bg-gray-900 border border-gray-700 rounded-lg text-white text-sm">
              {previousOwner.name}
              {previousOwner.purchaserName
                ? ` · ${labels.saleVerb} ${previousOwner.purchaserName}`
                : ''}
              {' · '}
              {fmtMonthLong(previousOwner.saleMonth)}
            </div>
          </div>
          <div>
            <label className="block text-[0.625rem] uppercase tracking-wider text-gray-500 font-semibold mb-2">
              As-of month
            </label>
            <select
              value={effectivePeriod}
              onChange={(e) => onSelectPeriod(e.target.value)}
              className="w-full px-3 py-2 bg-gray-900 border border-gray-700 rounded-lg text-white text-sm"
            >
              {[...dropdownMonths].reverse().map((m) => (
                <option key={m} value={m}>
                  {fmtMonthLong(m)}
                </option>
              ))}
            </select>
          </div>
        </div>
      </SectionShell>

      {/* HERO RETENTION BAR */}
      <section className="bg-gradient-to-br from-emerald-500/10 to-emerald-500/0 border border-emerald-500/30 rounded-xl p-5">
        <div className="flex items-center justify-between mb-3">
          <div>
            <div className="text-[0.625rem] uppercase tracking-wider text-emerald-400 font-semibold">
              Retention rate · {fmtMonthLong(summary.currentMonth)} vs {fmtMonthLong(summary.saleMonth)} baseline
            </div>
            <div className="mt-1 text-xs text-gray-300 tabular-nums">
              {formatCurrency(summary.currentRetainedCents)} retained from{' '}
              {formatCurrency(summary.baselineCents)} baseline ·{' '}
              {summary.monthsSinceSale} month{summary.monthsSinceSale === 1 ? '' : 's'} since sale
            </div>
          </div>
          <div className="text-5xl font-bold tabular-nums text-white">
            {summary.retentionPct.toFixed(2)}
            <span className="text-2xl text-emerald-300">%</span>
          </div>
        </div>
        <div
          className="h-2 rounded"
          style={{
            background: `linear-gradient(90deg, #10b981 0%, #10b981 ${summary.retentionPct}%, rgba(255,255,255,0.06) ${summary.retentionPct}%, rgba(255,255,255,0.06) 100%)`,
          }}
        />
        <div className="mt-2 flex justify-between text-[0.625rem] text-gray-500">
          <span>0%</span>
          <span>25%</span>
          <span>50%</span>
          <span>75%</span>
          <span>100%</span>
        </div>
      </section>

      {/* 4 STAT CARDS */}
      <section className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
        <StatCard
          label="Baseline @ sale"
          value={fmtMoneyShort(summary.baselineCents)}
          sub={`${summary.baselineLoanCount} loans · ${fmtMonthLabel(summary.saleMonth)}`}
        />
        <StatCard
          label="Active now"
          value={fmtMoneyShort(summary.currentRetainedCents)}
          sub={`${summary.currentRetainedLoanCount} loans · still on book`}
          tone="positive"
        />
        <StatCard
          label="Lost since sale"
          value={String(summary.lostCumulativeCount)}
          sub={`${fmtMoneyDelta(-summary.lostCumulativeCents)} cumulative drop`}
          tone="negative"
        />
        <StatCard
          label={`Last 30 days · ${periodLong}`}
          value={`${summary.netLoanChangeLastMonth >= 0 ? '+' : ''}${summary.netLoanChangeLastMonth}`}
          sub="Net loans this month"
          tone={summary.netLoanChangeLastMonth >= 0 ? 'positive' : 'negative'}
        />
      </section>

      {/* RETENTION CURVE TABLE */}
      <SectionShell
        title="Retention curve — since sale"
        subtitle={`Each month re-evaluates the pre-sale loan set against current balances. ${labels.agreementType} thresholds overlay here once configured.`}
        collapsible
      >
        <div className="overflow-x-auto bg-gray-900/40 rounded-lg">
          <table className="min-w-full text-sm">
            <thead>
              <tr className="text-left text-[0.625rem] uppercase tracking-wider text-gray-500 border-b border-gray-700/40">
                <th className="px-3 py-2 font-semibold">Month</th>
                <th className="px-3 py-2 font-semibold text-right">Active loans</th>
                <th className="px-3 py-2 font-semibold text-right">Retained balance</th>
                <th className="px-3 py-2 font-semibold text-right">vs baseline</th>
                <th className="px-3 py-2 font-semibold text-right">MoM Δ</th>
                <th className="px-3 py-2 font-semibold">Retention</th>
              </tr>
            </thead>
            <tbody>
              {retentionCurve.map((r, i) => {
                const isBaseline = i === 0;
                const isCurrent = r.month === data.periodMonth;
                const vsBaseline = r.retainedCents - summary.baselineCents;
                const pctClass =
                  r.retentionPct >= 95
                    ? 'text-emerald-300'
                    : r.retentionPct >= 85
                      ? 'text-emerald-400'
                      : r.retentionPct >= 75
                        ? 'text-amber-400'
                        : 'text-rose-400';
                return (
                  <tr
                    key={r.month}
                    className={`border-b border-gray-700/20 ${isCurrent ? 'bg-blue-500/5' : ''}`}
                  >
                    <td
                      className={`px-3 py-1.5 tabular-nums ${isCurrent ? 'text-white font-semibold' : 'text-gray-200'}`}
                    >
                      {fmtMonthLong(r.month)}
                      {isBaseline && (
                        <span className="text-gray-500 text-[0.625rem]"> ← baseline</span>
                      )}
                      {isCurrent && !isBaseline && (
                        <span className="text-blue-300 text-[0.625rem]"> ← current</span>
                      )}
                    </td>
                    <td className="px-3 py-1.5 tabular-nums text-right text-gray-300">
                      {r.retainedLoans}
                    </td>
                    <td className="px-3 py-1.5 tabular-nums text-right text-white">
                      {formatCurrency(r.retainedCents)}
                    </td>
                    <td
                      className={`px-3 py-1.5 tabular-nums text-right ${
                        isBaseline
                          ? 'text-gray-500'
                          : vsBaseline >= 0
                            ? 'text-emerald-300'
                            : 'text-rose-300'
                      }`}
                    >
                      {isBaseline ? '—' : fmtMoneyDelta(vsBaseline)}
                    </td>
                    <td
                      className={`px-3 py-1.5 tabular-nums text-right ${
                        isBaseline
                          ? 'text-gray-500'
                          : r.momDeltaCents >= 0
                            ? 'text-emerald-300'
                            : 'text-rose-300'
                      }`}
                    >
                      {isBaseline ? '—' : fmtMoneyDelta(r.momDeltaCents)}
                    </td>
                    <td className="px-3 py-1.5">
                      <div className="flex items-center gap-2">
                        <div
                          className="h-2 rounded flex-1 max-w-[180px]"
                          style={{
                            background: `linear-gradient(90deg, #10b981 0%, #10b981 ${r.retentionPct}%, rgba(255,255,255,0.06) ${r.retentionPct}%, rgba(255,255,255,0.06) 100%)`,
                          }}
                        />
                        <span className={`tabular-nums text-xs ${pctClass}`}>
                          {r.retentionPct.toFixed(2)}%
                        </span>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </SectionShell>

      {/* PER-LOAN MONTH-BY-MONTH GRID */}
      <BalanceGridSection
        rows={balanceGrid}
        months={monthsCovered}
        currentMonth={data.periodMonth}
      />

      {/* LOANS THAT LEFT THE BOOK */}
      <LoansThatLeftSection
        rows={data.loansThatLeft}
        isAdmin={isAdmin}
        onSetDisposition={onSetDisposition}
      />

      {/* DROPPED + REINSTATED THIS PERIOD */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <DroppedThisPeriodSection
          rows={data.droppedThisPeriod}
          periodLong={periodLong}
          isAdmin={isAdmin}
          onSetDisposition={onSetDisposition}
        />
        <ReinstatedSection rows={data.reinstatedThisPeriod} periodLong={periodLong} />
      </div>

      {/* POST-SALE ACTIVITY */}
      {data.postSaleActivity.length > 0 && (
        <PostSaleActivitySection rows={data.postSaleActivity} />
      )}

      {/* UPFRONTS */}
      {data.upfrontCommissionsThisPeriod.length > 0 && (
        <UpfrontsSection rows={data.upfrontCommissionsThisPeriod} periodLong={periodLong} />
      )}

      {/* CLAWBACKS */}
      {data.clawbacksThisPeriod.length > 0 && (
        <ClawbacksSection rows={data.clawbacksThisPeriod} periodLong={periodLong} />
      )}

      {/* FOOTER */}
      <footer className="bg-gray-900/40 border border-white/10 rounded-lg p-3">
        <p className="text-[0.625rem] text-gray-500 text-center">
          Report is in {data.featureFlagMode === 'observe' ? 'preview' : 'live'} mode · generated{' '}
          {fmtTimestamp(data.generatedAtIso)} Brisbane time
        </p>
      </footer>
    </>
  );
};

// ============================================================================
// BALANCE GRID
// ============================================================================

const BalanceGridSection: React.FC<{
  rows: BalanceGridRow[];
  months: string[];
  currentMonth: string;
}> = ({ rows, months, currentMonth }) => {
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState<'all' | 'active' | 'lost'>('all');

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return rows
      .filter((r) => {
        if (filter === 'active' && r.status === 'lost') return false;
        if (filter === 'lost' && r.status !== 'lost') return false;
        if (!q) return true;
        return (
          (r.client ?? '').toLowerCase().includes(q) ||
          r.loanId.toLowerCase().includes(q) ||
          (r.lender ?? '').toLowerCase().includes(q)
        );
      })
      .sort((a, b) => (b.balancesCents[currentMonth] ?? 0) - (a.balancesCents[currentMonth] ?? 0))
      .slice(0, 50);
  }, [rows, search, filter, currentMonth]);

  const statusBadge = (status: BalanceGridRow['status']): React.ReactNode => {
    if (status === 'active') {
      return (
        <span className="px-1.5 py-0.5 rounded text-[0.55rem] font-bold bg-emerald-500/15 text-emerald-300">
          ACTIVE
        </span>
      );
    }
    if (status === 'paydown') {
      return (
        <span className="px-1.5 py-0.5 rounded text-[0.55rem] font-bold bg-amber-500/15 text-amber-300">
          PAYDOWN
        </span>
      );
    }
    return (
      <span className="px-1.5 py-0.5 rounded text-[0.55rem] font-bold bg-rose-500/15 text-rose-300">
        LOST
      </span>
    );
  };

  const rightSlot = (
    <TableFilterBar
      search={search}
      onSearchChange={setSearch}
      placeholder="Search client or loan ID…"
      filterValue={filter}
      filterOptions={[
        { value: 'all', label: `All loans (${rows.length})` },
        { value: 'active', label: 'Still active' },
        { value: 'lost', label: 'Lost' },
      ]}
      onFilterChange={(v) => setFilter(v as 'all' | 'active' | 'lost')}
    />
  );

  return (
    <SectionShell
      title="Book value — per-loan history"
      subtitle="Each row is a loan from the pre-sale book. Each column is end-of-month balance. Empty (—) means the loan wasn't on the book that month (paid off, refinanced, or transferred)."
      accent="blue"
      rightSlot={rightSlot}
      collapsible
    >
      <div className="overflow-x-auto bg-gray-900/40 rounded-lg">
        <table className="min-w-full text-xs">
          <thead>
            <tr className="text-left text-[0.625rem] uppercase tracking-wider text-gray-500 border-b border-gray-700/40">
              <th className="sticky left-0 bg-gray-900/95 px-3 py-2 font-semibold z-10">Client</th>
              <th className="px-3 py-2 font-semibold">Loan ID</th>
              <th className="px-3 py-2 font-semibold">Lender</th>
              <th className="px-3 py-2 font-semibold">Status</th>
              {months.map((m) => (
                <th
                  key={m}
                  className={`px-3 py-2 font-semibold text-right ${m === currentMonth ? 'bg-blue-500/10' : ''}`}
                >
                  {fmtMonthLabel(m)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {filtered.map((r) => (
              <tr key={r.loanId} className="border-b border-gray-700/15 hover:bg-white/[0.02]">
                <td className="sticky left-0 bg-gray-900/95 px-3 py-1.5 text-gray-200">
                  {r.client ?? '—'}
                </td>
                <td className="px-3 py-1.5 font-mono text-gray-400">{r.loanId}</td>
                <td className="px-3 py-1.5 text-gray-300">{r.lender ?? '—'}</td>
                <td className="px-3 py-1.5">{statusBadge(r.status)}</td>
                {months.map((m) => {
                  const bal = r.balancesCents[m] ?? 0;
                  const isCurrent = m === currentMonth;
                  return (
                    <td
                      key={m}
                      className={`px-3 py-1.5 tabular-nums text-right ${
                        isCurrent ? 'font-semibold bg-blue-500/5 text-white' : 'text-gray-300'
                      } ${bal === 0 ? 'italic text-gray-500' : ''}`}
                    >
                      {bal === 0 ? '—' : fmtMoneyShort(bal)}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="mt-3 text-[0.625rem] text-gray-500">
        Showing {filtered.length} of {rows.length} baseline loans · sorted by current balance,
        highest first · live numbers, no upload required
      </p>
    </SectionShell>
  );
};

// ============================================================================
// LOANS THAT LEFT
// ============================================================================

const LoansThatLeftSection: React.FC<{
  rows: DroppedOffLoan[];
  isAdmin: boolean;
  onSetDisposition: (
    loanId: string,
    disposition: ManualDisposition,
    note?: string,
  ) => Promise<void>;
}> = ({ rows, isAdmin, onSetDisposition }) => {
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState<'all' | 'needs_review' | 'discharged' | 'refinanced' | 'manual'>('all');

  const counts = useMemo(() => {
    let discharged = 0;
    let refi = 0;
    let needsReview = 0;
    let overridden = 0;
    for (const r of rows) {
      if (r.override) overridden += 1;
      if (r.effectiveDisposition === 'discharged') discharged += 1;
      else if (r.effectiveDisposition === 'refinanced_internal') refi += 1;
      else if (r.effectiveDisposition === 'needs_review') needsReview += 1;
    }
    return { discharged, refi, needsReview, overridden };
  }, [rows]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return rows
      .filter((r) => {
        if (filter === 'needs_review' && r.effectiveDisposition !== 'needs_review') return false;
        if (filter === 'discharged' && r.effectiveDisposition !== 'discharged') return false;
        if (
          filter === 'refinanced' &&
          r.effectiveDisposition !== 'refinanced_internal' &&
          r.effectiveDisposition !== 'refinanced_external'
        )
          return false;
        if (filter === 'manual' && !r.override) return false;
        if (!q) return true;
        return (
          (r.client ?? '').toLowerCase().includes(q) ||
          r.loanId.toLowerCase().includes(q) ||
          (r.lender ?? '').toLowerCase().includes(q)
        );
      })
      .slice(0, 50);
  }, [rows, search, filter]);

  const totalLost = rows.reduce((s, r) => s + r.lastSeenBalanceCents, 0);

  const rightSlot = (
    <TableFilterBar
      search={search}
      onSearchChange={setSearch}
      placeholder="Search client, loan ID, lender…"
      filterValue={filter}
      filterOptions={[
        { value: 'all', label: `All (${rows.length})` },
        { value: 'needs_review', label: `Needs review (${counts.needsReview})` },
        { value: 'discharged', label: `Discharged (${counts.discharged})` },
        { value: 'refinanced', label: `Refinanced (${counts.refi})` },
        { value: 'manual', label: `Manually classified (${counts.overridden})` },
      ]}
      onFilterChange={(v) => setFilter(v as typeof filter)}
    />
  );

  return (
    <SectionShell
      title={`Loans that left the book · ${rows.length} cumulative · ${fmtMoneyDelta(-totalLost)}`}
      subtitle="In the pre-sale set, no longer producing trail commission. Refinances, full payouts, transfers."
      accent="rose"
      rightSlot={rightSlot}
      collapsible
    >
      {/* Disposition legend — stacked one per line for clarity */}
      <div className="bg-gray-900/40 rounded-lg p-3 mb-2 text-[0.7rem] text-gray-400 space-y-1.5">
        <div className="flex flex-col gap-1">
          <span>
            <span className="text-emerald-400 font-semibold">✓ Discharged</span> — automatic · the
            lender confirmed the loan was closed
          </span>
          <span>
            <span className="text-blue-400 font-semibold">↔ Refinanced (internal)</span> —
            automatic · same client has a new loan with another of your lenders
          </span>
          <span>
            <span className="text-amber-400 font-semibold">⚠ Needs review</span> — default · use the
            dropdown after a quick lender-portal check
          </span>
          <span>
            <span className="text-blue-300 font-semibold">↔ Refinanced externally</span> · manual ·
            client moved their loan to a lender outside your panel
          </span>
          <span>
            <span className="text-cyan-300 font-semibold">💤 Active, zero balance</span> · manual ·
            still alive (offset / paydown / lender feed delay)
          </span>
          <span>
            <span className="text-gray-300 font-semibold">✎ Other</span> · manual · pick this and
            add your own note explaining what happened
          </span>
        </div>
        <p className="text-gray-500 italic pt-1 border-t border-gray-700/40">
          {isAdmin
            ? 'Your dropdown choice is saved per loan, audit-logged with your name and time, and stays locked for every future month unless you change it again.'
            : 'Auto-detected statuses are shown. Manual overrides can only be set by admins.'}
        </p>
      </div>

      {rows.length === 0 ? (
        <EmptyState message="No loans have left the book since the sale." />
      ) : (
        <>
          <div className="overflow-x-auto bg-gray-900/40 rounded-lg">
            <table className="min-w-full text-xs">
              <thead>
                <tr className="text-left text-[0.625rem] uppercase tracking-wider text-gray-500 border-b border-gray-700/40">
                  <th className="px-3 py-2 font-semibold">Client</th>
                  <th className="px-3 py-2 font-semibold">Loan ID</th>
                  <th className="px-3 py-2 font-semibold">Lender</th>
                  <th className="px-3 py-2 font-semibold text-right">Last balance</th>
                  <th className="px-3 py-2 font-semibold">Last seen</th>
                  <th className="px-3 py-2 font-semibold">Disposition</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((r) => (
                  <tr key={r.loanId} className="border-b border-gray-700/20">
                    <td className="px-3 py-1.5 text-gray-200 align-top">{r.client ?? '—'}</td>
                    <td className="px-3 py-1.5 font-mono text-gray-400 align-top">{r.loanId}</td>
                    <td className="px-3 py-1.5 text-gray-200 align-top">{r.lender ?? '—'}</td>
                    <td className="px-3 py-1.5 tabular-nums text-right text-white align-top">
                      {formatCurrency(r.lastSeenBalanceCents)}
                    </td>
                    <td className="px-3 py-1.5 text-gray-400 align-top">
                      {fmtMonthLabel(r.lastSeenMonth)}
                    </td>
                    <td className="px-3 py-1.5 align-top">
                      <DispositionControl loan={r} canEdit={isAdmin} onSet={onSetDisposition} />
                      {r.refinanceHint && r.effectiveDisposition === 'refinanced_internal' && (
                        <div className="text-[0.625rem] text-gray-500 mt-0.5">
                          → {r.refinanceHint.newLender ?? '?'} ·{' '}
                          <span className="font-mono">{r.refinanceHint.newLoanId}</span>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="mt-2 text-[0.625rem] text-gray-500">
            Showing {filtered.length} of {rows.length} loans · sorted by last balance, highest first
            · {counts.discharged + counts.refi} automatically classified ·{' '}
            <span className="text-amber-400">{counts.needsReview}</span> need review ·{' '}
            {counts.overridden} manually overridden
          </p>
        </>
      )}
    </SectionShell>
  );
};

// ============================================================================
// DROPPED THIS PERIOD
// ============================================================================

const DroppedThisPeriodSection: React.FC<{
  rows: DroppedOffLoan[];
  periodLong: string;
  isAdmin: boolean;
  onSetDisposition: (
    loanId: string,
    disposition: ManualDisposition,
    note?: string,
  ) => Promise<void>;
}> = ({ rows, periodLong, isAdmin, onSetDisposition }) => {
  const [search, setSearch] = useState('');
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows.slice(0, 25);
    return rows
      .filter(
        (r) =>
          (r.client ?? '').toLowerCase().includes(q) ||
          r.loanId.toLowerCase().includes(q) ||
          (r.lender ?? '').toLowerCase().includes(q),
      )
      .slice(0, 25);
  }, [rows, search]);

  return (
    <SectionShell
      title={`Dropped in ${periodLong} · ${rows.length} loans`}
      subtitle={`From the pre-sale book, had a balance last month but zero in ${periodLong}.`}
      collapsible
      rightSlot={
        rows.length > 0 ? (
          <TableFilterBar
            search={search}
            onSearchChange={setSearch}
            placeholder="Search client or loan ID…"
          />
        ) : undefined
      }
    >
      {rows.length === 0 ? (
        <EmptyState message={`No drop-offs in ${periodLong}.`} />
      ) : (
        <table className="min-w-full text-xs">
          <thead>
            <tr className="text-left text-[0.625rem] uppercase tracking-wider text-gray-500 border-b border-gray-700/40">
              <th className="px-3 py-2 font-semibold">Client</th>
              <th className="px-3 py-2 font-semibold">Loan ID</th>
              <th className="px-3 py-2 font-semibold text-right">Last balance</th>
              <th className="px-3 py-2 font-semibold">Disposition</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((l) => (
              <tr key={l.loanId} className="border-b border-gray-700/20">
                <td className="px-3 py-1.5 text-gray-200 align-top">{l.client ?? '—'}</td>
                <td className="px-3 py-1.5 font-mono text-gray-400 align-top">{l.loanId}</td>
                <td className="px-3 py-1.5 tabular-nums text-right text-white align-top">
                  {formatCurrency(l.lastSeenBalanceCents)}
                </td>
                <td className="px-3 py-1.5 align-top">
                  <DispositionControl loan={l} canEdit={isAdmin} onSet={onSetDisposition} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </SectionShell>
  );
};

// ============================================================================
// REINSTATED THIS PERIOD
// ============================================================================

const ReinstatedSection: React.FC<{
  rows: ReinstatedLoan[];
  periodLong: string;
}> = ({ rows, periodLong }) => {
  const [search, setSearch] = useState('');
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows.slice(0, 25);
    return rows
      .filter(
        (r) =>
          (r.client ?? '').toLowerCase().includes(q) ||
          r.loanId.toLowerCase().includes(q) ||
          (r.lender ?? '').toLowerCase().includes(q),
      )
      .slice(0, 25);
  }, [rows, search]);

  return (
    <SectionShell
      title={`Reinstated in ${periodLong} · ${rows.length} loans`}
      subtitle={`No balance last month but reappeared in ${periodLong} — typically lender reporting delays or interest-capitalisation events.`}
      collapsible
      rightSlot={
        rows.length > 0 ? (
          <TableFilterBar
            search={search}
            onSearchChange={setSearch}
            placeholder="Search client or loan ID…"
          />
        ) : undefined
      }
    >
      {rows.length === 0 ? (
        <EmptyState message={`No reinstated loans in ${periodLong}.`} />
      ) : (
        <table className="min-w-full text-xs">
          <thead>
            <tr className="text-left text-[0.625rem] uppercase tracking-wider text-gray-500 border-b border-gray-700/40">
              <th className="px-3 py-2 font-semibold">Client</th>
              <th className="px-3 py-2 font-semibold">Loan ID</th>
              <th className="px-3 py-2 font-semibold text-right">Current</th>
              <th className="px-3 py-2 font-semibold">Last seen</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((l) => (
              <tr key={l.loanId} className="border-b border-gray-700/20">
                <td className="px-3 py-1.5 text-gray-200">{l.client ?? '—'}</td>
                <td className="px-3 py-1.5 font-mono text-gray-400">{l.loanId}</td>
                <td className="px-3 py-1.5 tabular-nums text-right text-amber-300">
                  {formatCurrency(l.currentBalanceCents)}
                </td>
                <td className="px-3 py-1.5 text-gray-400">{fmtMonthLabel(l.lastSeenMonth)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </SectionShell>
  );
};

// ============================================================================
// POST-SALE ACTIVITY
// ============================================================================

const PostSaleActivitySection: React.FC<{
  rows: PostSaleActivityRow[];
}> = ({ rows }) => {
  const [search, setSearch] = useState('');
  const totalLoans = rows.reduce((s, r) => s + r.loansWritten, 0);
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) => r.brokerName.toLowerCase().includes(q));
  }, [rows, search]);

  return (
    <SectionShell
      title={`Post-sale activity · ${totalLoans} new loans across ${rows.length} brokers`}
      subtitle="Loans written after the sale, grouped by broker. Excluded from the retention metric; shown for context."
      collapsible
      rightSlot={
        <TableFilterBar
          search={search}
          onSearchChange={setSearch}
          placeholder="Search broker…"
        />
      }
    >
      <table className="min-w-full text-xs">
        <thead>
          <tr className="text-left text-[0.625rem] uppercase tracking-wider text-gray-500 border-b border-gray-700/40">
            <th className="px-3 py-2 font-semibold">Broker</th>
            <th className="px-3 py-2 font-semibold text-right">Loans written</th>
            <th className="px-3 py-2 font-semibold text-right">Current $</th>
            <th className="px-3 py-2 font-semibold">First active</th>
          </tr>
        </thead>
        <tbody>
          {filtered.map((r) => (
            <tr key={r.brokerName} className="border-b border-gray-700/20">
              <td className="px-3 py-1.5 text-gray-200">{r.brokerName}</td>
              <td className="px-3 py-1.5 tabular-nums text-right text-white">{r.loansWritten}</td>
              <td className="px-3 py-1.5 tabular-nums text-right text-blue-300">
                {fmtMoneyShort(r.currentBalanceCents)}
              </td>
              <td className="px-3 py-1.5 text-gray-400">{fmtMonthLong(r.firstActiveMonth)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </SectionShell>
  );
};

// ============================================================================
// UPFRONTS
// ============================================================================

const UpfrontsSection: React.FC<{
  rows: UpfrontCommissionRow[];
  periodLong: string;
}> = ({ rows, periodLong }) => {
  const [search, setSearch] = useState('');
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    const base = q
      ? rows.filter(
          (r) =>
            (r.brokerName ?? '').toLowerCase().includes(q) ||
            (r.client ?? '').toLowerCase().includes(q) ||
            (r.lender ?? '').toLowerCase().includes(q) ||
            r.loanId.toLowerCase().includes(q),
        )
      : rows;
    return base.slice(0, 50);
  }, [rows, search]);

  return (
    <SectionShell
      title={`Upfront commissions paid in ${periodLong} · ${rows.length} settlements`}
      subtitle={`New settlements by your brokers in ${periodLong} — picked up automatically from the upfront commissions in your data. Not part of the retention metric.`}
      collapsible
      rightSlot={
        <TableFilterBar
          search={search}
          onSearchChange={setSearch}
          placeholder="Search broker, client, lender…"
        />
      }
    >
      <table className="min-w-full text-xs">
        <thead>
          <tr className="text-left text-[0.625rem] uppercase tracking-wider text-gray-500 border-b border-gray-700/40">
            <th className="px-3 py-2 font-semibold">Broker</th>
            <th className="px-3 py-2 font-semibold">Client</th>
            <th className="px-3 py-2 font-semibold">Lender</th>
            <th className="px-3 py-2 font-semibold">Settled</th>
            <th className="px-3 py-2 font-semibold text-right">Loan amount</th>
            <th className="px-3 py-2 font-semibold text-right">Upfront net</th>
          </tr>
        </thead>
        <tbody>
          {filtered.map((r) => (
            <tr key={r.loanId} className="border-b border-gray-700/20">
              <td className="px-3 py-1.5 text-gray-200">{r.brokerName ?? '—'}</td>
              <td className="px-3 py-1.5 text-gray-200">{r.client ?? '—'}</td>
              <td className="px-3 py-1.5 text-gray-200">{r.lender ?? '—'}</td>
              <td className="px-3 py-1.5 text-gray-400">{r.settlementDate ?? '—'}</td>
              <td className="px-3 py-1.5 tabular-nums text-right text-white">
                {formatCurrency(r.loanAmountCents)}
              </td>
              <td className="px-3 py-1.5 tabular-nums text-right text-emerald-300">
                {formatCurrency(r.upfrontNetCents)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </SectionShell>
  );
};

// ============================================================================
// CLAWBACKS
// ============================================================================

const ClawbacksSection: React.FC<{
  rows: ClawbackRow[];
  periodLong: string;
}> = ({ rows, periodLong }) => {
  const [search, setSearch] = useState('');
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    const base = q
      ? rows.filter(
          (r) =>
            (r.client ?? '').toLowerCase().includes(q) ||
            (r.lender ?? '').toLowerCase().includes(q) ||
            r.loanId.toLowerCase().includes(q) ||
            (r.note ?? '').toLowerCase().includes(q),
        )
      : rows;
    return base.slice(0, 50);
  }, [rows, search]);

  return (
    <SectionShell
      title={`Clawbacks & reversals in ${periodLong} · ${rows.length} reversals`}
      subtitle={`Negative commissions in ${periodLong} where the lender reclaimed an upfront because the loan was rescinded or refinanced within the clawback window.`}
      collapsible
      rightSlot={
        <TableFilterBar
          search={search}
          onSearchChange={setSearch}
          placeholder="Search client, lender, notes…"
        />
      }
    >
      <table className="min-w-full text-xs">
        <thead>
          <tr className="text-left text-[0.625rem] uppercase tracking-wider text-gray-500 border-b border-gray-700/40">
            <th className="px-3 py-2 font-semibold">Client</th>
            <th className="px-3 py-2 font-semibold">Lender</th>
            <th className="px-3 py-2 font-semibold">Invoiced</th>
            <th className="px-3 py-2 font-semibold text-right">Original</th>
            <th className="px-3 py-2 font-semibold text-right">Reversal</th>
            <th className="px-3 py-2 font-semibold text-right">Clawback %</th>
            <th className="px-3 py-2 font-semibold">Notes</th>
          </tr>
        </thead>
        <tbody>
          {filtered.map((r) => (
            <tr key={r.loanId} className="border-b border-gray-700/20">
              <td className="px-3 py-1.5 text-gray-200">{r.client ?? '—'}</td>
              <td className="px-3 py-1.5 text-gray-200">{r.lender ?? '—'}</td>
              <td className="px-3 py-1.5 text-gray-400">{r.invoicedDate ?? '—'}</td>
              <td className="px-3 py-1.5 tabular-nums text-right text-gray-300">
                {formatCurrency(r.originalUpfrontCents)}
              </td>
              <td className="px-3 py-1.5 tabular-nums text-right text-rose-300">
                {formatCurrency(r.netReversalCents)}
              </td>
              <td className="px-3 py-1.5 tabular-nums text-right text-rose-200">
                {r.clawbackPct == null ? '—' : `${r.clawbackPct.toFixed(1)}%`}
              </td>
              <td className="px-3 py-1.5 text-xs text-gray-400 italic">{r.note ?? '—'}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </SectionShell>
  );
};

export default BrokerBookTrendReportPage;
